import {
  getObservationPrimaryCode,
  type WearableObservationSectionId,
} from "@/utils/wearableObservationFormat";

/**
 * Ordem de exibição das métricas (visão do dia + gráficos de período).
 * Índices menores aparecem primeiro. Códigos/labels desconhecidos ficam no fim.
 */

/** Dados gerais: Passos → FC → PA → Calorias → Água → Glicose. */
const GENERAL_LOINC_ORDER = [
  "55423-8", // Passos
  "8867-4", // Frequência cardíaca
  "85354-9", // Pressão arterial
  "9052-2", // Calorias
  "8999-5", // Água (ingestão)
  "2339-0", // Glicose no sangue
] as const;

/**
 * Sono por LOINC (linhas expandidas usam também METRIC_LABEL_ORDER).
 * Sono total → leve → profundo → REM → acordado → SpO2 <90% → SpO2 → temperatura.
 */
const SLEEP_LOINC_ORDER = [
  "93832-4", // Sono total
  "93830-8", // Sono leve
  "93831-6", // Sono profundo
  "93829-0", // Sono REM
  "93828-2", // Sono acordado
  "96634-8", // SpO2 abaixo de 90%
  "59408-5", // SpO2 (mínima / média)
  "81264-4", // Temperatura corporal
] as const;

/**
 * Composição corporal: Peso → Altura → Massa muscular esquelética → demais.
 */
const BODY_COMPOSITION_LOINC_ORDER = [
  "29463-7", // Peso
  "8302-2", // Altura
  "73964-9", // Massa muscular esquelética
  "73965-6", // % músculo esquelético
  "41982-0", // % gordura
  "73708-0", // Massa de gordura
  "8342-8", // Massa livre de gordura
  "101683-1", // Água corporal total
  "39156-5", // IMC
  "41979-6", // Taxa metabólica basal
] as const;

/**
 * Ordem fina por rótulo (linhas derivadas + rótulos amigáveis).
 * Usada depois da expansão em buildSectionView / séries do período.
 */
const METRIC_LABEL_ORDER = [
  // Dados gerais
  "passos",
  "frequência cardíaca",
  "frequencia cardiaca",
  "pressão arterial",
  "pressao arterial",
  "calorias",
  "água (ingestão)",
  "agua (ingestao)",
  "água (ingestao)",
  "agua (ingestão)",
  "glicose no sangue",
  "glicose",
  // Sono total
  "sono total · duração",
  "sono total · início",
  "sono total · fim",
  // Estágios
  "sono leve",
  "sono: leve",
  "sono profundo",
  "sono: profundo",
  "sono rem",
  "sono: rem",
  "sono acordado",
  "sono: acordado",
  // SpO2
  "spo2 abaixo de 90%",
  "spo2 · mínima",
  "spo2 mínima",
  "spo2 · média",
  "spo2 média",
  "spo2",
  // Temperatura (mín → média → máx)
  "temperatura corporal · mínima",
  "temperatura corporal · média",
  "temperatura corporal · máxima",
  // Composição corporal
  "peso",
  "altura",
  "massa muscular esquelética",
  "massa muscular esqueletica",
  "% músculo esquelético",
  "% musculo esqueletico",
  "% gordura",
  "massa de gordura",
  "massa livre de gordura",
  "água corporal total",
  "agua corporal total",
  "imc",
  "taxa metabólica basal",
  "taxa metabolica basal",
] as const;

const SECTION_LOINC_ORDER: Partial<
  Record<WearableObservationSectionId, readonly string[]>
> = {
  general: GENERAL_LOINC_ORDER,
  sleep: SLEEP_LOINC_ORDER,
  bodyComposition: BODY_COMPOSITION_LOINC_ORDER,
};

function indexOrEnd(list: readonly string[], key: string): number {
  const idx = list.indexOf(key);
  return idx === -1 ? list.length : idx;
}

/** Índice de ordenação pelo código LOINC da Observation (entrada FHIR). */
export function observationSortIndex(
  resource: any,
  sectionId: WearableObservationSectionId
): number {
  const order = SECTION_LOINC_ORDER[sectionId];
  if (!order) return 0;
  return indexOrEnd(order, getObservationPrimaryCode(resource));
}

/**
 * Índice de ordenação por rótulo exibido (métrica do dia ou série do gráfico).
 * Preferência: match exato no catálogo; senão tenta por LOINC embutido na key.
 */
export function metricLabelSortIndex(label: string, seriesKey?: string): number {
  const normalized = label.trim().toLowerCase();

  // Match exato no catálogo de rótulos
  const exact = METRIC_LABEL_ORDER.indexOf(
    normalized as (typeof METRIC_LABEL_ORDER)[number]
  );
  if (exact !== -1) return exact;

  // Prefixo só com limite de palavra (evita "água" casar "água corporal total")
  for (let i = 0; i < METRIC_LABEL_ORDER.length; i++) {
    const needle = METRIC_LABEL_ORDER[i];
    if (
      normalized === needle ||
      normalized.startsWith(`${needle} `) ||
      normalized.startsWith(`${needle}·`) ||
      normalized.startsWith(`${needle} ·`)
    ) {
      return i;
    }
  }

  // Fallback: LOINC na key da série (ex.: "93830-8" ou "81264-4:min")
  if (seriesKey) {
    const code = seriesKey.split(":")[0];
    for (const order of Object.values(SECTION_LOINC_ORDER)) {
      if (!order) continue;
      const idx = order.indexOf(code);
      if (idx !== -1) {
        // Desloca para o fim do bloco de labels (após temperatura) + posição LOINC
        return METRIC_LABEL_ORDER.length + idx;
      }
    }
  }

  return METRIC_LABEL_ORDER.length + 500;
}

/** Ordena entradas FHIR de uma seção pela ordem canônica de LOINC. */
export function sortObservationEntries(
  entries: any[],
  sectionId: WearableObservationSectionId
): any[] {
  // Sem ordem definida (tela / exercício): mantém a ordem de chegada
  if (!SECTION_LOINC_ORDER[sectionId]) return entries;

  return [...entries].sort((a, b) => {
    const diff =
      observationSortIndex(a?.resource, sectionId) -
      observationSortIndex(b?.resource, sectionId);
    if (diff !== 0) return diff;
    return 0;
  });
}

/** Ordena linhas/séries já com rótulo amigável. */
export function sortByMetricLabel<T extends { label: string; key?: string }>(
  items: T[]
): T[] {
  return [...items].sort((a, b) => {
    const ia = metricLabelSortIndex(a.label, a.key);
    const ib = metricLabelSortIndex(b.label, b.key);
    if (ia !== ib) return ia - ib;
    // Empate sem rótulo conhecido: preserva ordem de chegada (stable sort)
    if (ia >= METRIC_LABEL_ORDER.length) return 0;
    return a.label.localeCompare(b.label, "pt-BR");
  });
}
